import { ShieldCheck, ChevronDown, Check } from 'lucide-react';
import { useRole, type Role } from '@/lib/role';
import { TEAMS } from '@/lib/teams';
import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';

function CustomDropdown({ value, options, onChange, align = 'left' }: { value: string, options: {value: string, label: string}[], onChange: (val: string) => void, align?: 'left' | 'right' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const click = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', click);
    return () => document.removeEventListener('mousedown', click);
  }, []);

  const selected = options.find(o => o.value === value) || options[0];

  return (
    <div ref={ref} className="relative inline-block text-left">
      <button 
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded bg-transparent px-2 py-1 text-xs font-bold text-theme hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
      >
        <span className="truncate max-w-[120px]">{selected.label}</span>
        <ChevronDown className="h-3.5 w-3.5 opacity-50" />
      </button>

      {open && (
        <div className={cn(
          "absolute top-full mt-1 z-50 min-w-[180px] overflow-hidden rounded-xl border border-[rgba(255,255,255,0.05)] bg-[var(--color-bg)] shadow-xl neo p-1",
          align === 'right' ? "right-0" : "left-0"
        )}>
          <div className="max-h-64 overflow-y-auto">
            {options.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-xs font-medium transition-colors",
                  opt.value === value ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]" : "text-theme hover:bg-black/5 dark:hover:bg-white/5"
                )}
              >
                {opt.label}
                {opt.value === value && <Check className="h-3.5 w-3.5" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Demo-only "view as" control. In production the role comes from profiles.role. */
export function RoleSwitcher() {
  const { role, teamId, setRole, setTeamId } = useRole();

  const roleOptions = [
    { value: 'Super Admin', label: 'Super Admin' },
    { value: 'Admin', label: 'Admin' },
    { value: 'Manager', label: 'Manager' },
    { value: 'Agent', label: 'Agent' },
    { value: 'Technician', label: 'Technician' },
    { value: 'Viewer', label: 'Viewer' },
    { value: 'User', label: 'User' },
    { value: 'Normal User', label: 'Normal User' },
    { value: 'Service Desk Agent', label: 'Service Desk Agent' },
    { value: 'Team Lead', label: 'Team Lead' },
    { value: 'Department Head', label: 'Department Head' },
    { value: 'SOC Analyst', label: 'SOC Analyst' },
    { value: 'Auditor', label: 'Auditor' },
  ];

  const teamOptions = TEAMS.map(t => ({ value: t.id, label: t.name }));

  return (
    <div className="hidden items-center gap-1 rounded-full neo-inset px-3 py-1 lg:flex" title="View as (demo) — production reads profiles.role">
      <ShieldCheck className="h-4 w-4 text-muted mx-1" />
      <CustomDropdown
        value={role || 'Super Admin'}
        options={roleOptions}
        onChange={(val) => {
          const r = val as Role;
          setRole(r);
          if (r !== 'Super Admin' && r !== 'Admin' && !teamId) setTeamId(TEAMS[0].id);
        }}
      />
      
      {role !== 'Super Admin' && role !== 'Admin' && (
        <>
          <span className="text-muted mx-1 opacity-50">·</span>
          <CustomDropdown
            value={teamId ?? TEAMS[0].id}
            options={teamOptions}
            onChange={setTeamId}
            align="right"
          />
        </>
      )}
    </div>
  );
}
